import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SkeletonModule } from 'primeng/skeleton';
import { UsersUserResponse } from '../../../../../../../../../libs/shared/generated/src/lib/types/users';
import { PreEmploymentActivitiesService } from '../../../../../services/pre-employment-activities.service';


@Component({
  selector: 'app-pre-employment-file-upload-dialog',
  imports: [
    CommonModule,
    ButtonModule,
    DialogModule,
    TranslateModule,
    SkeletonModule
  ],
  templateUrl: './pre-employment-file-upload-dialog.component.html',
  styleUrl: './pre-employment-file-upload-dialog.component.scss'
})
export class PreEmploymentFileUploadDialogComponent implements OnInit {
  @Input() open: boolean = false;
  @Input() me: UsersUserResponse | null = null;
  @Output() close: EventEmitter<void> = new EventEmitter<void>();
  @Output() submitted: EventEmitter<void> = new EventEmitter<void>();
  @Input() activity: any;
  @Input() requestId?: string | null = null;
  selectedFile: File | null = null
  isCompleted: boolean = false
  imagePreviewUrl: string | null = null;
  imageAttachmentFileKey: string = '';
  fileName: string | undefined = '';
  hasNewFile: boolean = false;
  isLoadingDetail: boolean = true;
  isUploading: boolean = false;
  private readonly ALLOWED_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
  private readonly ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf'];

  constructor(
    private readonly translate: TranslateService,
    private readonly messageService: MessageService,
    private readonly preEmploymentActivitiesService: PreEmploymentActivitiesService,
  ) {
  }
  ngOnInit(): void {
    if (!this.requestId) {
      this.isLoadingDetail = false;
      return;
    }
    this.preEmploymentActivitiesService.getActivityDetail(this.requestId).subscribe({
      next: (response) => {
        this.isLoadingDetail = false;
        const submission = response.submission;
        if (!submission || submission.activityKind !== 'FILE_UPLOAD') return;

        this.isCompleted = true;
        this.imageAttachmentFileKey = submission.uploadedFile.key;
        this.imagePreviewUrl = submission.uploadedFile.presignedUrl;
        this.fileName = submission.uploadedFile.fileName;

      },
      error: () => {
        this.isLoadingDetail = false
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('global.error.label'),
          detail: this.translate.instant('pre-employment.file-upload-dialog.generic.error.detail'),
        })
      },
    });
  }

  onFileUpload(event: any): Promise<string | undefined> {
    return new Promise((resolve) => {
      const file = event.target.files[0];
      if (!file) { resolve(undefined); return; }
      const extension = file.name.split('.').pop()?.toLowerCase();
      const isValidFormat = this.ALLOWED_TYPES.includes(file.type) && this.ALLOWED_EXTENSIONS.includes(extension)
      if (!isValidFormat) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('pre-employment.file-upload-dialog.toast-warn-message-invalid-format.summary'),
          detail: this.translate.instant('pre-employment.file-upload-dialog.toast-warn-message-invalid-format.detail'),
        });
        resolve(undefined)
        return;
      }
      if (file) {
        this.isUploading = true
        this.preEmploymentActivitiesService.getPresignedUrlForImageUpload(this.activity.id).subscribe({
          next: (res) => {
            const { key, presignedUrl } = res;
            const contentType = file ? file.type : 'image/jpeg'
            if (!contentType) {
              resolve(undefined)
              return;
            }
            fetch(presignedUrl, {
              method: 'PUT',
              body: file,
              headers: {
                'Content-Type': contentType,
              },
            })
              .then((awsRes) => {
                if (!awsRes.ok) throw new Error("Aws upload failed");
                this.isUploading = false
                this.selectedFile = file
                this.fileName = file.name;
                this.imagePreviewUrl = URL.createObjectURL(file);
                this.imageAttachmentFileKey = key;
                this.hasNewFile = true;
                resolve(key);
              })
              .catch(() => {
                this.isUploading = false
                resolve(undefined)
              });
          },
          error: () => {
            this.isUploading = false
            resolve(undefined)
          },
        })
      }
    })
  }

  save(): void {
    if (!this.requestId || !this.hasNewFile || !this.imageAttachmentFileKey) return

    const request$ = this.isCompleted
      ? this.preEmploymentActivitiesService.updateFileUploadSubmission(this.requestId, {
        fileKey: this.imageAttachmentFileKey,
        fileName: this.fileName,
      })
      : this.preEmploymentActivitiesService.submitActivity(this.requestId, {
        activityKind: 'FILE_UPLOAD',
        fileKey: this.imageAttachmentFileKey,
        fileName: this.fileName,
      });

    request$.subscribe({
      next: () => {
        this.submitted.emit();
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('global.success.label'),
          detail: this.translate.instant('pre-employment.file-upload-dialog.toast-success-submit.detail'),
        }); this.onClose();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('global.error.label'),
          detail: this.translate.instant('pre-employment-file-upload-dialog.toast-error-message'),
        });
      },
    });
  }


  onClose(): void {
    this.isCompleted = false
    this.close.emit()
    URL.revokeObjectURL(this.imagePreviewUrl || '')
    this.selectedFile = null
    this.imagePreviewUrl = null
    this.imageAttachmentFileKey = ''
    this.fileName = ''
  }
  onRemoveFile(): void {
    this.selectedFile = null
    URL.revokeObjectURL(this.imagePreviewUrl || '')
    this.imagePreviewUrl = null
    this.imageAttachmentFileKey = ''
    this.hasNewFile = false
    this.fileName = ''
  }
  onOpenPdf(): void {
    if (!this.imagePreviewUrl) return
    window.open(this.imagePreviewUrl, '_blank', 'noopener')
  }
}
